import { useQuery } from '@tanstack/react-query';
import { fetchProTxCount, loadCachedTxCount, getPricingTier, EARLY_PRICE_USD } from '@/lib/pro';

const BCH_USD_CACHE_KEY = 'bch-pos:pro-bch-usd';

/** Persist last known BCH/USD price for instant upgrade page loads */
function persistBCHUSD(price: number): void {
  try { localStorage.setItem(BCH_USD_CACHE_KEY, JSON.stringify({ price, updatedAt: Date.now() })); }
  catch { /* ignore */ }
}

/** Load last known BCH/USD price from localStorage */
function loadCachedBCHUSD(): number | undefined {
  try {
    const raw = localStorage.getItem(BCH_USD_CACHE_KEY);
    if (!raw) return undefined;
    const parsed = JSON.parse(raw) as { price: number; updatedAt: number };
    return parsed.price;
  } catch { return undefined; }
}

async function fetchBCHUSD(): Promise<number> {
  const res = await fetch('https://api.kraken.com/0/public/Ticker?pair=BCHUSD');
  if (!res.ok) throw new Error(`Kraken fetch failed: ${res.status}`);
  const data = await res.json();
  if (data.error?.length) throw new Error(`Kraken error: ${data.error[0]}`);
  const price = parseFloat(data.result?.BCHUSD?.c?.[0]);
  if (isNaN(price) || price <= 0) throw new Error('Invalid Kraken price');
  persistBCHUSD(price);
  return price;
}

export function useProPricing() {
  const { data: txCount } = useQuery({
    queryKey: ['pro-tx-count'],
    queryFn: fetchProTxCount,
    staleTime: 2 * 60 * 1000,
    refetchInterval: 3 * 60 * 1000,
    retry: 1,
    initialData: loadCachedTxCount,
    initialDataUpdatedAt: 0,     // always considered stale → refetch in background
  });

  const { data: bchUsdPrice, isLoading: priceLoading, isError: priceError } = useQuery({
    queryKey: ['bch-usd-price'],
    queryFn: fetchBCHUSD,
    staleTime: 60 * 1000,
    refetchInterval: 90 * 1000,
    retry: 2,
    initialData: loadCachedBCHUSD,
    initialDataUpdatedAt: 0,     // always considered stale → refetch in background
  });

  // The upgrade PRICE only depends on the BCH/USD rate and the pricing tier.
  // The purchase count merely selects the tier (early vs regular) and the
  // "spots left" counter. So we gate the price on bchUsdPrice ONLY — if the
  // tx-count lookup fails or is slow (e.g. explorer unreachable), we still
  // render a usable price by assuming the customer-favorable early tier.
  //
  // This is important because the tx count comes from a serverless proxy that
  // is only live in production; without this decoupling the upgrade button
  // would hang on "Fetching live price…" whenever the count is unavailable.

  const priceKnown = bchUsdPrice !== undefined;
  const isLoading = !priceKnown && priceLoading;
  const isError = !priceKnown && priceError;

  if (bchUsdPrice === undefined) {
    return {
      isLoading,
      isError,
      txCount: undefined,
      priceBCH: undefined,
      priceUSD: EARLY_PRICE_USD,
      isEarly: true,
      spotsLeft: undefined,
      bchUsdPrice: undefined,
    };
  }

  // If txCount is unknown, assume the early tier (best price) and leave
  // spotsLeft undefined so the UI can hide the live counter gracefully.
  const effectiveTxCount = txCount ?? 0;
  const tier = getPricingTier(effectiveTxCount, bchUsdPrice);

  return {
    isLoading: false,
    isError: false,
    txCount,
    bchUsdPrice,
    priceBCH: tier.priceBCH,
    priceUSD: tier.priceUSD,
    isEarly: tier.isEarly,
    // Only expose spotsLeft when we actually have a verified count.
    spotsLeft: txCount === undefined ? undefined : tier.spotsLeft,
  };
}
